import { Injectable, OnDestroy } from '@angular/core';

// Local types mirroring command-center-ui/shared/src/lib/event-bus/contracts.ts
// Keep in sync with that file. See EVENT_BUS_GUIDE.md for details.

export interface EventContract<T = unknown> {
    readonly type: string;
    readonly source: string;
    readonly payload?: T;
    readonly timestamp: number;
    readonly correlationId?: string;
    readonly metadata?: Record<string, unknown>;
}

/** Subscription token returned by the event bus */
interface SubscriptionToken {
    unsubscribe: () => void;
}

/** Minimal event bus API surface exposed on window.__EVENT_BUS__ */
export interface EventBus {
    subscribe: (
        type: string,
        handler: (event: EventContract) => void,
        options?: Record<string, unknown>
    ) => SubscriptionToken;
    publish: (event: {
        type: string;
        source: string;
        payload?: unknown;
        correlationId?: string;
    }) => void;
    getStats?: () => Record<string, unknown>;
    getHistory?: () => EventContract[];
}

// Payload interfaces — keep in sync with command-center-ui contracts.ts

export interface ShellAuthLoginPayload {
    user: { id?: string; name?: string; email?: string; [key: string]: unknown };
    token: string;
    tokenType?: string;
    expiresAt: number;
}

export interface ShellAuthLogoutPayload {
    reason?: 'user' | 'timeout' | 'error';
}

export interface ShellAuthTokenRefreshedPayload {
    token: string;
    expiresAt: number;
}

export interface ShellAuthErrorPayload {
    reason: string;
    details?: string;
    timestamp: number;
}

export interface ShellOrganizationChangedPayload {
    organizationId: string;
    organizationName: string;
    organizationLabel?: string;
}

export interface ShellConfigUpdatedPayload {
    locale?: string;
    apiBaseUrl?: string;
}

export interface MfeReadyPayload {
    name: string;
    version: string;
    capabilities?: string[];
}

export interface MfeAuthStateRequestPayload {
    source: string;
}

export interface WebgovNavigationRequestPayload {
    path: string;
}

export interface WebgovNavigationChangedPayload {
    /** Internal route within the MFE, e.g. '/customers/abc-123' */
    internalRoute: string;
    /** Full path including shell prefix, e.g. '/webgov/customers/abc-123' */
    fullPath: string;
    /** Human-readable breadcrumb label, e.g. 'Acquia Inc' */
    breadcrumbLabel?: string;
}

/** Event type constants matching command-center-ui EVENT_TYPES */
export const EVENT_TYPES = Object.freeze({
    // Shell events
    SHELL_INITIALIZED: 'shell.initialized',
    SHELL_THEME_CHANGED: 'shell.theme.changed',
    SHELL_USER_UPDATED: 'shell.user.updated',
    SHELL_CONFIG_UPDATED: 'shell.config.updated',
    SHELL_ORGANIZATION_CHANGED: 'shell.organization.changed',

    // Shell auth events
    SHELL_AUTH_LOGIN: 'shell.auth.login',
    SHELL_AUTH_LOGOUT: 'shell.auth.logout',
    SHELL_AUTH_TOKEN_REFRESHED: 'shell.auth.token.refreshed',
    SHELL_AUTH_ERROR: 'shell.auth.error',
    MFE_AUTH_STATE_REQUEST: 'mfe.auth.state.request',

    // MFE lifecycle
    MFE_READY: 'mfe.ready',
    MFE_MOUNTED: 'mfe.mounted',
    MFE_UNMOUNTED: 'mfe.unmounted',
    MFE_ERROR: 'mfe.error',

    // Navigation
    NAVIGATION_REQUEST: 'navigation.request',
    NAVIGATION_CHANGED: 'navigation.changed',

    // Menu registration
    WEBGOV_MENU_READY: 'webgov.menu.ready',

    // Shell → WebGov navigation requests
    WEBGOV_NAVIGATION_REQUEST: 'webgov.navigation.request',

    // WebGov → Shell navigation notifications
    WEBGOV_NAVIGATION_CHANGED: 'webgov.navigation.changed',
} as const);

// Augment Window so TypeScript knows about __EVENT_BUS__
declare global {
    interface Window {
        __EVENT_BUS__?: EventBus;
    }
}

/**
 * Bridges events between WebGov MFE and Command Center shell via window.__EVENT_BUS__.
 * No-ops gracefully in standalone mode. See EVENT_BUS_GUIDE.md.
 */
@Injectable({ providedIn: 'root' })
export class EventBusBridgeService implements OnDestroy {
    private readonly bus: EventBus | undefined;
    private subscriptions: SubscriptionToken[] = [];
    private readonly source = 'webgov-mfe';

    constructor () {
        this.bus = typeof window === 'undefined' ? undefined : window.__EVENT_BUS__;
    }

    /** Whether the event bus is available (i.e. running inside Command Center shell) */
    get isConnected (): boolean {
        return this.bus !== undefined;
    }

    /** Subscribe to a shell/MFE event by type */
    subscribe<T = unknown>(eventType: string, handler: (event: EventContract<T>) => void): void {
        if (!this.bus) {
            return;
        }
        const token = this.bus.subscribe(eventType, handler as (event: EventContract) => void);
        this.subscriptions.push(token);
    }

    /** Publish an event to the shell */
    publish<T = unknown>(type: string, payload: T): void {
        if (!this.bus) {
            return;
        }
        this.bus.publish({ type, source: this.source, payload });
    }

    /** Request current auth state from the shell */
    requestAuthState (): void {
        this.publish<MfeAuthStateRequestPayload>(EVENT_TYPES.MFE_AUTH_STATE_REQUEST, {
            source: this.source,
        });
    }

    /** Ask the shell to log the user out */
    requestLogout (reason: ShellAuthLogoutPayload['reason'] = 'error'): void {
        this.publish<ShellAuthLogoutPayload>(EVENT_TYPES.SHELL_AUTH_LOGOUT, { reason });
    }

    /** Announce this MFE is ready */
    announceReady (): void {
        this.publish<MfeReadyPayload>(EVENT_TYPES.MFE_READY, {
            name: 'WebGov',
            version: '1.0.0',
        });
    }

    /** Publish menu items to the shell. Pass empty array to clear menu. */
    publishMenuReady (items: Array<{ label: string; route: string; icon?: string }>): void {
        this.publish(EVENT_TYPES.WEBGOV_MENU_READY, { items });
    }

    /** Notify the shell that WebGov has navigated to a new route */
    publishNavigationChanged (payload: WebgovNavigationChangedPayload): void {
        this.publish<WebgovNavigationChangedPayload>(EVENT_TYPES.WEBGOV_NAVIGATION_CHANGED, payload);
    }

    /** Cleanup all subscriptions */
    ngOnDestroy (): void {
        this.subscriptions.forEach((t) => t.unsubscribe());
        this.subscriptions = [];
    }
}
